import { GridRow, GridRowProps } from '@mui/x-data-grid-pro';
import { memo, useCallback, useMemo, Fragment, useState, forwardRef } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { useSearch } from '../SearchContextProvider';
import SearchLinkWrapper from './SearchLinkWrapper';
import { getRecordRealId, useSearchItems } from './SearchItemsProvider';
import {
  MiniMenuSelector,
  TMenuState,
  TSetMenuEvent,
} from 'components/Minimenus/MiniMenuSelector';
import { useResourceContext } from 'react-admin';
import { canSelectSearchItem } from '../utility/searchItemSelection';
import { SearchRowMenuContext, SearchRowMenuContextValue } from './SearchRowActionsCell';
import { isRowSelected } from 'utility/selectionModelUtils';

// namespaced prop to avoid collision with mui props
// can props here as needed replacing [key:string]:any
// or prefix props with some unique prefix eg 'customOnClick'
// or if need to can direct override mui props...
export interface SearchGridRowProps extends GridRowProps {
  customProps?: {
    [key: string]: any;
  };
}

export const SearchGridRow = memo(
  forwardRef<HTMLDivElement, SearchGridRowProps>(
    ({ customProps, ...muiRowProps }, ref) => {
      const reactKey = `SearchGridRow_${muiRowProps?.rowId}`;
      const api = useGridApiContext();
      const rowData = api.current.getRow(muiRowProps?.rowId);
      const { getSearchItemInfo, isModalSearch } = useSearch();
      const resource = useResourceContext();
      const rowItemInfo = getSearchItemInfo(rowData);
      const {
        recordMap,
        setSelectedItems,
        selectedItems,
        selectedItemIds,
        selectItem,
        deselectById,
        clearSelectedItems,
        hasSelectionEnabled,
        hasMiniMenuEnabled,
        miniMenuConfig,
        miniMenuHandlers,
      } = useSearchItems();

      // mouse context event state for right click
      const [menuEvent, setMenuEvent] = useState<TMenuState>(null);
      // anchor element state for button-triggered menu
      const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

      const handleContextMenu: TSetMenuEvent = useCallback(
        (event) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (!hasSelectionEnabled && !hasMiniMenuEnabled) {
            console.warn(
              '[handleContextMenu] Selection is disabled; cannot open context menu.',
            );
            if (menuEvent) {
              setMenuEvent(null); // clear menu if was already open somehow
            }
            return; // no menu if selection is disabled
          }
          // Show menu without selecting the item
          const recId = getRecordRealId(rowData, resource);
          const record = recordMap.get(recId);
          if (!record) {
            console.warn(`[handleContextMenu] No data found in map with ID: ${recId}`);
            return;
          }
          setMenuEvent(event);
        },
        [
          menuEvent,
          hasSelectionEnabled,
          recordMap,
          resource,
          rowData,
          hasMiniMenuEnabled,
        ],
      );

      const handleMiniMenuClose = useCallback(() => {
        setMenuEvent(null);
        setAnchorEl(null);
      }, []);

      const openMenuAtAnchor = useCallback((el: HTMLElement) => {
        setAnchorEl(el);
      }, []);

      const rowMenuCtx = useMemo<SearchRowMenuContextValue>(
        () => ({ openMenuAtAnchor }),
        [openMenuAtAnchor],
      );

      // Handle row click for modal search to select/deselect
      const handleRowClick = useCallback(() => {
        if (!hasSelectionEnabled && !hasMiniMenuEnabled) {
          return;
        }

        // Check if the item is disabled (only applies in modal mode)
        if (!canSelectSearchItem(rowItemInfo, isModalSearch)) {
          return;
        }

        const recordId = getRecordRealId(rowData, resource);
        if (!recordId) {
          return;
        }
        if (isRowSelected(selectedItemIds, recordId)) {
          deselectById(recordId);
        } else {
          const record = recordMap.get(recordId);
          if (record) {
            selectItem(record);
          }
        }
      }, [
        hasSelectionEnabled,
        rowItemInfo,
        isModalSearch,
        rowData,
        resource,
        selectedItemIds,
        deselectById,
        recordMap,
        selectItem,
        hasMiniMenuEnabled,
      ]);

      return (
        <Fragment key={reactKey}>
          <SearchRowMenuContext.Provider value={rowMenuCtx}>
            <SearchLinkWrapper
              data-testid={`search-result-item-${muiRowProps?.rowId}`}
              record={rowData}
              to={rowItemInfo?.url}
              target={rowItemInfo?.target}
              onContextMenu={handleContextMenu}
              // For modal search, use handleRowClick; otherwise, let SearchLinkWrapper handle navigation
              onClick={isModalSearch ? handleRowClick : undefined}
            >
              <GridRow
                ref={ref}
                {...muiRowProps}
              />
            </SearchLinkWrapper>
          </SearchRowMenuContext.Provider>
          <MiniMenuSelector
            miniProps={{
              menuEvent,
              setMenuEvent,
              handleMiniMenuClose,
              clearSelectedItems,
              setSelectedItems,
              selectedItems,
              raResource: resource,
              gridApiRef: api,
              contextMenuItem: rowData, // Pass the right-clicked item
              isContextItemDisabled: rowItemInfo?.disabled, // Pass disabled state
              configMap: miniMenuConfig,
              anchorEl,
              ...miniMenuHandlers,
            }}
          />
        </Fragment>
      );
    },
  ),
);

SearchGridRow.displayName = 'SearchGridRow';
